import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Calendar,
  User,
  Share2,
  BookOpen,
  ChevronRight
} from 'lucide-react';

import { blogs } from '../data/blogs';

const BlogPostPage = () => {

  const { postId } = useParams();

  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  const [post, setPost] = useState(null);

  useEffect(() => {

    const foundPost = blogs.find(
      (item) => item.post_id === postId
    );

    setPost(foundPost);

  }, [postId]);

  useEffect(() => {

    if (post) {

      document.title = isHindi
        ? post.title_hi
        : post.title_en;

    }

  }, [post, isHindi]);

  if (!post) {

    return (

      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">

        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C9A84C]"></div>

      </div>

    );
  }

  return (

    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-20">

      <div className="max-w-5xl mx-auto">

        {/* BACK BUTTON */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#C9A84C] hover:text-[#E5C870] transition-colors mb-10"
        >

          <ArrowLeft size={18} />

          {isHindi
            ? 'ब्लॉग पर वापस जाएँ'
            : 'Back to Blog'}

        </Link>

        {/* FEATURE IMAGE */}
        <div className="relative rounded-3xl overflow-hidden mb-10 border border-[#C9A84C]/10">

          <img
  src={post.image}
  alt={isHindi ? post.title_hi : post.title_en}
  className="w-full h-[500px] object-cover"
/>


          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

          <div className="absolute bottom-8 left-8">

            <span className="inline-block bg-[#C9A84C]/20 backdrop-blur-md text-[#F5F2EB] px-5 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
              {post.category}
            </span>

            <h1 className="text-4xl md:text-5xl font-serif text-[#F5F2EB] leading-tight max-w-4xl">

              {isHindi
                ? post.title_hi
                : post.title_en}

            </h1>

          </div>

        </div>

        {/* META SECTION */}
        <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-6 mb-10">

          <div className="flex flex-wrap items-center justify-between gap-5">

            <div className="flex flex-wrap items-center gap-6 text-[#A3A3A3]">

              <div className="flex items-center gap-2">

                <User size={18} className="text-[#C9A84C]" />

                <span>{post.author}</span>

              </div>

              <div className="flex items-center gap-2">

                <Calendar size={18} className="text-[#C9A84C]" />

                <span>{post.created_at}</span>

              </div>

              <div className="flex items-center gap-2">

                <BookOpen size={18} className="text-[#C9A84C]" />

                <span>{post.category}</span>

              </div>

            </div>

            <button className="flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#222] border border-[#C9A84C]/10 px-5 py-3 rounded-full text-[#C9A84C] transition-all">

              <Share2 size={16} />

              <span>
                {isHindi
                  ? 'शेयर करें'
                  : 'Share'}
              </span>

            </button>

          </div>

        </div>

        {/* BLOG CONTENT */}
        <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8 md:p-12">

          <div className="prose prose-invert max-w-none">

            <div className="text-[#D4D4D4] text-lg leading-loose whitespace-pre-line">

              {isHindi
                ? post.content_hi
                : post.content_en}

            </div>

          </div>

        </div>

        {/* QUOTE SECTION */}
        <div className="mt-12 bg-gradient-to-r from-[#1A1A1A] to-[#111111] border border-[#C9A84C]/10 rounded-3xl p-10">

          <div className="flex items-start gap-4">

            <div className="w-1 bg-[#C9A84C] rounded-full min-h-[120px]"></div>

            <div>

              <h3 className="text-2xl font-serif text-[#F5F2EB] mb-4">

                {isHindi
                  ? 'आध्यात्मिक संदेश'
                  : 'Spiritual Insight'}

              </h3>

              <p className="text-[#A3A3A3] leading-loose text-lg italic">

                {isHindi
                  ? 'ज्योतिष और वास्तु केवल भविष्य जानने का माध्यम नहीं, बल्कि जीवन में सही दिशा, संतुलन और सकारात्मक ऊर्जा प्राप्त करने का मार्ग है।'
                  : 'Astrology and Vastu are not merely systems of prediction — they are sacred sciences that guide individuals toward balance, clarity, positivity, and spiritual growth.'}

              </p>

            </div>

          </div>

        </div>

        {/* NEXT BLOGS */}
        <div className="mt-20">

          <div className="flex items-center gap-3 mb-10">

            <ChevronRight className="text-[#C9A84C]" />

            <h2 className="text-3xl font-serif text-[#F5F2EB]">

              {isHindi
                ? 'अन्य ब्लॉग'
                : 'Related Articles'}

            </h2>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {blogs
              .filter((item) => item.post_id !== post.post_id)
              .slice(0, 2)
              .map((relatedPost) => (

                <Link
                  key={relatedPost.post_id}
                  to={`/blog/${relatedPost.post_id}`}
                  className="group bg-[#111111] border border-[#C9A84C]/10 rounded-3xl overflow-hidden hover:border-[#C9A84C]/30 transition-all hover:-translate-y-1"
                >

                  <div className="h-52 overflow-hidden">

                    <img
  src={relatedPost.image}
  alt={relatedPost.title_en}
  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
/>


                  </div>

                  <div className="p-6">

                    <span className="text-[#C9A84C] text-xs uppercase tracking-wider">
                      {relatedPost.category}
                    </span>

                    <h3 className="text-2xl font-serif text-[#F5F2EB] mt-3 group-hover:text-[#C9A84C] transition-colors leading-snug">

                      {isHindi
                        ? relatedPost.title_hi
                        : relatedPost.title_en}

                    </h3>

                  </div>

                </Link>

              ))}

          </div>

        </div>

      </div>

    </div>

  );
};

export default BlogPostPage;